import { create } from "zustand";
import { CHAPTER_COUNT } from "../data/experience";
import { stateForChapter, wrapChapter } from "../lib/experience-machine";
import { detectQualityTier } from "../lib/quality";
import {
  EMPTY_CAMERA_SIGNAL,
  type CameraSignal,
  type CameraStatus,
  type ExperienceState,
  type QualityTier,
} from "../types/exhibition";

interface ExperienceStore {
  readonly state: ExperienceState;
  readonly chapterIndex: number;
  readonly progress: number;
  readonly chapterProgress: number;
  readonly quality: QualityTier;
  readonly cameraStatus: CameraStatus;
  readonly cameraSignal: CameraSignal;
  readonly autoPlay: boolean;
  readonly heroRotation: Readonly<{ x: number; y: number }>;
  readonly explosion: number;
  readonly scanPosition: number | null;
  readonly focusedProductIndex: number | null;
  setTimeline: (progress: number, chapterIndex: number, chapterProgress: number) => void;
  setChapter: (chapterIndex: number) => void;
  setCameraStatus: (status: CameraStatus) => void;
  setCameraSignal: (signal: CameraSignal) => void;
  setAutoPlay: (enabled: boolean) => void;
  setHeroRotation: (rotation: Readonly<{ x: number; y: number }>) => void;
  setExplosion: (value: number) => void;
  setScanPosition: (value: number | null) => void;
  setFocusedProductIndex: (index: number | null) => void;
  reset: () => void;
}

export const useExperienceStore = create<ExperienceStore>((set) => ({
  state: "attract",
  chapterIndex: 0,
  progress: 0,
  chapterProgress: 0,
  quality: typeof window === "undefined" ? "medium" : detectQualityTier(),
  cameraStatus: "idle",
  cameraSignal: EMPTY_CAMERA_SIGNAL,
  autoPlay: true,
  heroRotation: { x: 0, y: 0 },
  explosion: 0,
  scanPosition: null,
  focusedProductIndex: null,
  setTimeline: (progress, chapterIndex, chapterProgress) =>
    set((current) => ({
      progress,
      chapterIndex,
      chapterProgress,
      state: stateForChapter(chapterIndex),
      focusedProductIndex:
        chapterIndex === 4 ? current.focusedProductIndex : null,
      explosion: chapterIndex === 3 ? current.explosion : 0,
      scanPosition: chapterIndex === 2 ? current.scanPosition : null,
      heroRotation: chapterIndex === current.chapterIndex ? current.heroRotation : { x: 0, y: 0 },
    })),
  setChapter: (chapterIndex) => {
    const wrappedIndex = wrapChapter(chapterIndex, CHAPTER_COUNT);
    set((current) => ({
      chapterIndex: wrappedIndex,
      state: stateForChapter(wrappedIndex),
      focusedProductIndex:
        wrappedIndex === 4 ? current.focusedProductIndex : null,
    }));
  },
  setCameraStatus: (cameraStatus) => set({ cameraStatus }),
  setCameraSignal: (cameraSignal) => set({ cameraSignal }),
  setAutoPlay: (autoPlay) => set({ autoPlay }),
  setHeroRotation: (heroRotation) => set({ heroRotation }),
  setExplosion: (value) => set({ explosion: Math.min(1, Math.max(0, value)) }),
  setScanPosition: (value) => set({ scanPosition: value === null ? null : Math.min(1, Math.max(0, value)) }),
  setFocusedProductIndex: (focusedProductIndex) => set({ focusedProductIndex }),
  reset: () =>
    set({
      state: "attract",
      chapterIndex: 0,
      progress: 0,
      chapterProgress: 0,
      cameraSignal: EMPTY_CAMERA_SIGNAL,
      autoPlay: true,
      heroRotation: { x: 0, y: 0 },
      explosion: 0,
  scanPosition: null,
      focusedProductIndex: null,
    }),
}));
